import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tabs,
  Tab,
  Grid,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import axiosInstance from "../axiosInstance";
import { isAdminUser } from "../config/adminUsers";
import { getUserEmail } from "../utils/getUserEmail";
import {
  applyEnrichmentPreviewFieldEdit,
  createEnrichmentRuleDetail,
  evaluateEnrichmentRules,
  getEnrichmentPreviewErrorMessage,
  getEnrichmentPreviewSourceFieldEdit,
  requestEnrichmentPreview,
} from "../utils/enrichmentPreview";
import EnrichmentPreviewSourceEditor from "./EnrichmentPreviewSourceEditor";
import Autocomplete from "@mui/material/Autocomplete";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import TenantAutocomplete from "./TenantAutocomplete";
import TenantManagementModernLayout from "./TenantManagementModernLayout";
import {
  buildImportSettingsPayload,
  defaultImportSettings,
  normalizeImportSettings,
} from "../utils/importSettings";
// Custom TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}
// Accessibility props for tabs
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const resolveFeedDataType = (feedUrl, configuredDataType) => {
  const dataType = configuredDataType?.trim().toLowerCase();
  if (["csv", "json", "xml", "txt"].includes(dataType)) return dataType;

  const urlPath = feedUrl?.split(/[?#]/)[0].toLowerCase() || "";
  if (urlPath.endsWith(".json")) return "json";
  if (urlPath.endsWith(".xml")) return "xml";
  return "csv";
};

const getJsonItemsCollection = (data) => {
  if (Array.isArray(data)) {
    return { items: data, withItems: (items) => items };
  }

  if (!data || typeof data !== "object") return null;

  const entries = Object.entries(data);
  const preferredEntry = entries.find(
    ([key, value]) => ["products", "items", "data"].includes(key.toLowerCase()) && Array.isArray(value)
  );
  const firstArrayEntry = entries.find(([, value]) => Array.isArray(value));
  const [key, items] = preferredEntry || firstArrayEntry || [];

  if (!key) return null;
  return { items, withItems: (nextItems) => ({ ...data, [key]: nextItems }) };
};

const TENANT_SECTION_AREAS = {
  overview: "general",
  assets: "general",
  "order-tracking": "general",
  "data-index": "data",
  enrichment: "data",
  metafields: "data",
  taxonomy: "data",
  jobs: "data",
  caches: "data",
  "cache-v2": "data",
  "ai-profile": "ai-assistant",
  "feature-flags": "ai-assistant",
  integration: "ai-assistant",
  "widget-settings": "widget",
  "widget-theme": "widget",
  "pdp-theme": "widget",
  advanced: "advanced",
  "tenant-json": "advanced",
};

// A new component for displaying values that can be copied on click
const CopyableTypography = ({ label, value, onCopy }) => {
  const displayValue = value || "N/A";
  return (
    <Typography>
      <strong>{label}:</strong>{" "}
      <Box
        component="span"
        onClick={() => onCopy(displayValue)}
        sx={{
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
            color: "primary.main",
          },
          wordBreak: "break-all" // Added to handle long URLs
        }}
      >
        {displayValue}
      </Box>
    </Typography>
  );
};
const TenantsManagementTab = ({
  tenants,
  selectedTenant,
  setSelectedTenant,
  onAssistantContextChange,
}) => {
  const isAdmin = isAdminUser(getUserEmail());
  const [layoutMode, setLayoutMode] = useState(() => {
    if (typeof window === "undefined") return "new";
    return window.localStorage.getItem("tenant-management-layout-mode") || "new";
  });
  const [activeTenantSection, setActiveTenantSection] = useState("overview");
  const [expandedTenantSectionGroups, setExpandedTenantSectionGroups] = useState(["general"]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [tenantDetails, setTenantDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [deletingTenant, setDeletingTenant] = useState(false);
  const [savingTenant, setSavingTenant] = useState(false); // State for saving tenant
  const [tabValue, setTabValue] = useState(0);
  const [dialog, setDialog] = useState({ open: false, title: "", message: "" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  // State for Import Enrichment Rules
  const [importSettings, setImportSettings] = useState(null);
  const [isImportSettingsDirty, setIsImportSettingsDirty] = useState(false);
  const [availableFieldNames, setAvailableFieldNames] = useState([]);
  // State for the copy-to-clipboard snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  // State for clearing cache
  const [clearingCache, setClearingCache] = useState(false);
  // State for generated install URL
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  const [feedPreviewType, setFeedPreviewType] = useState("csv");
  const [jsonPreviewContent, setJsonPreviewContent] = useState(null);
  const [jsonPreviewData, setJsonPreviewData] = useState(null);
  const [loadingCsvPreview, setLoadingCsvPreview] = useState(false);
  const [csvSearchQuery, setCsvSearchQuery] = useState("");
  const [csvSelectedColumns, setCsvSelectedColumns] = useState([]);
  const [csvVisibleRows, setCsvVisibleRows] = useState(100);
  const [testRulesProductId, setTestRulesProductId] = useState("");
  const [testRulesResults, setTestRulesResults] = useState(null);
  const [testRulesRawProduct, setTestRulesRawProduct] = useState(null);
  const [testRulesLoading, setTestRulesLoading] = useState(false);
  const [ruleDetailRetesting, setRuleDetailRetesting] = useState(false);
  const [ruleDetailOpen, setRuleDetailOpen] = useState(false);
  const [ruleDetailData, setRuleDetailData] = useState(null);

  useEffect(() => {
    if (typeof onAssistantContextChange !== "function") return;

    const area = TENANT_SECTION_AREAS[activeTenantSection] || "general";
    onAssistantContextChange({
      Page: "tenants",
      TenantId: selectedTenantId || selectedTenant || null,
      areaId: `tenant-${area}`,
      SectionId: `tenant-${area}-${activeTenantSection}`,
      SubsectionId: null,
    });
  }, [
    activeTenantSection,
    onAssistantContextChange,
    selectedTenant,
    selectedTenantId,
  ]);

  const filteredJsonPreview = useMemo(() => {
    if (jsonPreviewContent === null) return null;

    const collection = getJsonItemsCollection(jsonPreviewData);
    const query = csvSearchQuery.trim().toLowerCase();

    if (!collection) {
      const content = jsonPreviewContent;
      const matches = !query || content.toLowerCase().includes(query);
      return { content: matches ? content : "{}", shownItems: matches ? 1 : 0, totalItems: 1 };
    }

    const visibleItems = query
      ? collection.items.filter((item) => JSON.stringify(item).toLowerCase().includes(query))
      : collection.items;

    return {
      content: JSON.stringify(collection.withItems(visibleItems), null, 2),
      shownItems: visibleItems.length,
      totalItems: collection.items.length,
    };
  }, [csvSearchQuery, jsonPreviewContent, jsonPreviewData]);

  const isAggregationRulePresent = useMemo(() => {
    return importSettings?.rules?.some(rule => rule.type === 'Aggregation') || false;
  }, [importSettings]);

  useEffect(() => {
    if (!dialog.open || typeof document === "undefined") return undefined;

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousDocumentOverflow = documentElement.style.overflow;
    const previousScrollbarGutter = documentElement.style.scrollbarGutter;

    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";
    documentElement.style.scrollbarGutter = "stable";

    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousDocumentOverflow;
      documentElement.style.scrollbarGutter = previousScrollbarGutter;
    };
  }, [dialog.open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const handleCopy = (text) => {
    if (text && text !== "N/A") {
      navigator.clipboard.writeText(text).then(
        () => {
          setSnackbarMessage(`Copied "${text}" to clipboard!`);
          setSnackbarOpen(true);
        },
        (err) => {
          setSnackbarMessage("Failed to copy text.");
          setSnackbarOpen(true);
          console.error("Could not copy text: ", err);
        }
      );
    }
  };
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  useEffect(() => {
    if (selectedTenant && selectedTenant !== selectedTenantId) {
      handleTenantSelect(selectedTenant);
      return;
    }

    if (!selectedTenant && selectedTenantId) {
      setSelectedTenantId("");
      setTenantDetails(null);
      setImportSettings(null);
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("tenant-management-layout-mode", layoutMode);
  }, [layoutMode]);
  const refreshTenants = async (newTenantId = "") => {
    try {
      await axiosInstance.get("/tenant/alltenants");
      if (newTenantId) {
        setSelectedTenantId(newTenantId);
        handleTenantSelect(newTenantId);
      }
    } catch (error) {
      console.error("Error refreshing tenants:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to refresh tenants. Please try again later.",
      });
    }
  };
  const handleClearCache = async () => {
    if (!selectedTenantId) {
      setDialog({
        open: true,
        title: "Error",
        message: "No tenant selected to clear cache for.",
      });
      return;
    }
    setClearingCache(true);
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    try {
      await axiosInstance.delete(`${apiUrl}/api/cache?prefix=${selectedTenantId}`, {
        headers: {
          'X-API-Key': '5f89d410-8eb2-46cf-beb9-6119fdf804f6',
          'Tenantid': selectedTenantId,
        },
      });
      setDialog({
        open: true,
        title: "Success",
        message: "Cache clear request sent successfully for the tenant.",
      });
    } catch (error) {
      console.error("Error clearing cache:", error);
      setDialog({
        open: true,
        title: "Error",
        message: error.response?.data?.message || "Failed to clear cache.",
      });
    } finally {
      setClearingCache(false);
    }
  };
  const handleTenantSelect = async (tenantId, { preserveCurrentView = false } = {}) => {
    setSelectedTenant(tenantId);
    setSelectedTenantId(tenantId);
    if (!preserveCurrentView) {
      setLoadingDetails(true);
      setTenantDetails(null);
      setJsonError(null);
      setGeneratedUrl("");
      setImportSettings(null);
      setAvailableFieldNames([]);
      setIsImportSettingsDirty(false);
      setTestRulesProductId("");
      setTestRulesResults(null);
      setTestRulesRawProduct(null);
      setTestRulesLoading(false);
      setRuleDetailRetesting(false);
      setRuleDetailOpen(false);
      setRuleDetailData(null);
    }
    setIsDraft(false);
    try {
      // Fetch tenant details first
      const tenantResponse = await axiosInstance.get(`/tenant/${tenantId}`);
      setTenantDetails(tenantResponse.data);
      // Try to fetch import settings, but don't fail if it doesn't exist
      try {
        const importSettingsResponse = await axiosInstance.get(`/tenant-import/enrichment-settings/${tenantId}`);

        if (importSettingsResponse.data) {
          const { importEnrichRules, fieldNames } = importSettingsResponse.data;
          setImportSettings(normalizeImportSettings(importEnrichRules));
          setAvailableFieldNames(fieldNames || []);
        } else {
          // Set default structure if no data
          setImportSettings({ ...defaultImportSettings });
          setAvailableFieldNames([]);
        }
      } catch (importError) {
        console.warn("Import settings not available:", importError);
        // Set default structure if enrichment endpoint doesn't exist or fails
        setImportSettings({ ...defaultImportSettings });
        setAvailableFieldNames([]);
      }
      // Check for a draft, which might overwrite the main details
      try {
        const draftResponse = await axiosInstance.get(`/tenant-draft/${tenantId}`);
        setIsDraft(true);
        setTenantDetails(draftResponse.data);
      } catch (draftError) {
        if (draftError.response && draftError.response.status === 404) {
        } else {
          throw draftError;
        }
      }
    } catch (error) {
      console.error("Error fetching tenant data:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to load tenant data. Please try again.",
      });
    } finally {
      if (!preserveCurrentView) {
        setLoadingDetails(false);
      }
    }
  };
  // Handlers for Import Settings Form
  const handleImportSettingTextFieldChange = (e) => {
    const { name, value } = e.target;
    setImportSettings(prev => ({ ...prev, [name]: value }));
    setIsImportSettingsDirty(true);
  };

  const handleHasParentProductChange = (event) => {
    const isChecked = event.target.checked;
    setImportSettings(prev => {
      const newSettings = { ...prev, hasParentProduct: isChecked };
      if (isChecked) {
        const hasParentRule = newSettings.rules?.some(rule => rule.type === 'ParentProductAggregation') || false;
        if (!hasParentRule) {
          newSettings.rules = [
            ...(newSettings.rules || []),
            { fieldName: '', type: 'ParentProductAggregation', script: '' }
          ];
        }
      } else {
        newSettings.rules = (newSettings.rules || []).filter(rule => rule.type !== 'ParentProductAggregation');
      }
      return newSettings;
    });
    setIsImportSettingsDirty(true);
  };
  const handleUsePreezieGeneratedIdChange = (event) => {
    setImportSettings(prev => ({ ...prev, usePreezieGeneratedId: event.target.checked }));
    setIsImportSettingsDirty(true);
  };
  const handleUseAggregationValueAsDocumentIdChange = (event) => {
    setImportSettings(prev => ({
      ...prev,
      useAggregationValueAsDocumentId: event.target.checked,
    }));
    setIsImportSettingsDirty(true);
  };
  const handleRuleChange = (index, field, value) => {
    const updatedRules = [...(importSettings?.rules || [])];
    const newRule = { ...updatedRules[index], [field]: value };
    // If the type is changed to Aggregation or ParentProductAggregation, clear the fieldName
    if (field === 'type' && (value === 'Aggregation' || value === 'ParentProductAggregation')) {
      newRule.fieldName = '';
    }
    updatedRules[index] = newRule;
    setImportSettings(prev => ({ ...prev, rules: updatedRules }));
    setIsImportSettingsDirty(true);
  };
  const handleAddRule = () => {
    const newRule = { fieldName: "", type: "FieldValue", script: "" };
    setImportSettings(prev => ({ ...prev, rules: [...(prev?.rules || []), newRule] }));
    setIsImportSettingsDirty(true);
  };
  const handleRemoveRule = (index) => {
    const updatedRules = (importSettings?.rules || []).filter((_, i) => i !== index);
    setImportSettings(prev => ({ ...prev, rules: updatedRules }));
    setIsImportSettingsDirty(true);
  };
  const handleResetImportSettings = () => {
    // This needs to be re-fetched or use the originally fetched data
    handleTenantSelect(selectedTenantId);
    setIsImportSettingsDirty(false);
  };
  const handleSaveImportSettings = async () => {
    try {
      const payload = buildImportSettingsPayload(importSettings);
      await axiosInstance.put(`/tenant-import/enrichment-settings/${selectedTenantId}`, payload);
      setDialog({
        open: true,
        title: "Success",
        message: "Import enrichment settings saved successfully!",
      });
      setIsImportSettingsDirty(false);
      // Refresh tenant data to reflect the changes
      await handleTenantSelect(selectedTenantId, { preserveCurrentView: true });
    } catch (error) {
      console.error("Error saving import settings:", error);
      setDialog({
        open: true,
        title: "Error",
        message: error.response?.data?.message || "Failed to save import settings.",
      });
    }
  };
  const handleDetailsChange = (value) => {
    try {
      const parsed = JSON.parse(value);
      setTenantDetails(parsed);
      setJsonError(null);
    } catch (error) {
      setJsonError("Invalid JSON format. Please correct it.");
    }
  };

  // Main Save Tenant Function (Direct Update)
  const handleSaveTenant = async () => {
    if (jsonError) {
      setDialog({
        open: true,
        title: "Error",
        message: "Cannot save. The JSON content is invalid.",
      });
      return;
    }
    setSavingTenant(true);
    try {
      const dataFeedConfigurations = tenantDetails?.dataFeedConnector?.configurations || {};
      const featureConfigurations = tenantDetails?.tenantAiProfile?.featureConfigurations || {};
      const {
        EnableProductSalesCount: legacyEnableProductSalesCount,
        ProductSalesLookbackDays: legacyProductSalesLookbackDays,
        ...dataFeedConfigurationsWithoutImporterMerchandising
      } = dataFeedConfigurations;
      const enableProductSalesCount =
        featureConfigurations.EnableProductSalesCount ?? legacyEnableProductSalesCount ?? true;
      const parsedProductSalesLookbackDays = Number.parseInt(
        featureConfigurations.ProductSalesLookbackDays ?? legacyProductSalesLookbackDays,
        10
      );
      const productSalesLookbackDays = Number.isFinite(parsedProductSalesLookbackDays)
        ? Math.min(60, Math.max(30, parsedProductSalesLookbackDays))
        : 30;
      const tenantPayload = {
        ...tenantDetails,
        tenantAiProfile: {
          ...tenantDetails?.tenantAiProfile,
          featureConfigurations: {
            ...featureConfigurations,
            EnableProductSalesCount:
              String(enableProductSalesCount).toLowerCase() === "true" ? "true" : "false",
            ProductSalesLookbackDays: String(productSalesLookbackDays),
          },
        },
        dataFeedConnector: {
          ...tenantDetails?.dataFeedConnector,
          configurations: {
            ...dataFeedConfigurationsWithoutImporterMerchandising,
            useGraphQLBulkQuery: String(dataFeedConfigurations.useGraphQLBulkQuery ?? false),
            useGraphQL: String(dataFeedConfigurations.useGraphQL ?? false),
          },
        },
      };

      // Sends the current tenant details, with importer settings persisted as strings.
      await axiosInstance.put(`/tenant/${selectedTenantId}`, tenantPayload);
      setDialog({
        open: true,
        title: "Success",
        message: "Tenant details saved successfully!",
      });
      // Refresh to ensure we have the latest server state
      await handleTenantSelect(selectedTenantId, { preserveCurrentView: true });
    } catch (error) {
      console.error("Error saving tenant:", error);
      let errorMessage = "Failed to save tenant details.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setDialog({ open: true, title: "Error", message: errorMessage });
    } finally {
      setSavingTenant(false);
    }
  };

  const handlePublishDraft = async () => {
    try {
      await axiosInstance.post(
        `/tenant-draft/${selectedTenantId}/publish-tenant-variables`,
        { DraftVersion: tenantDetails.draftVersion }
      );
      setDialog({
        open: true,
        title: "Success",
        message: "Draft published successfully!",
      });
      setIsDraft(false);
      handleTenantSelect(selectedTenantId);
    } catch (error) {
      console.error("Error publishing draft:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to publish draft.",
      });
    }
  };
  const handlePublishDraftWidgetConfigs = async () => {
    try {
      await axiosInstance.post(
        `/tenant-draft/${selectedTenantId}/publish-widget-config`,
        { DraftVersion: tenantDetails.draftVersion }
      );
      setDialog({
        open: true,
        title: "Success",
        message: "Draft published successfully!",
      });
      setIsDraft(false);
      handleTenantSelect(selectedTenantId);
    } catch (error) {
      console.error("Error publishing draft:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to publish draft.",
      });
    }
  };
  const handleDeleteDraft = async () => {
    try {
      await axiosInstance.delete(
        `/tenant-draft/${selectedTenantId}/${tenantDetails.draftVersion}/variables`
      );
      setDialog({
        open: true,
        title: "Success",
        message: "Draft deleted successfully!",
      });
      setIsDraft(false);
      handleTenantSelect(selectedTenantId);
    } catch (error) {
      console.error("Error deleting draft:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to delete draft.",
      });
    }
  };
  const handleDeleteDraftWidgetConfigs = async () => {
    try {
      await axiosInstance.delete(
        `/tenant-draft/${selectedTenantId}/${tenantDetails.draftVersion}/widget-config`
      );
      setDialog({
        open: true,
        title: "Success",
        message: "Draft deleted successfully!",
      });
      setIsDraft(false);
      handleTenantSelect(selectedTenantId);
    } catch (error) {
      console.error("Error deleting draft:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to delete draft.",
      });
    }
  };
  const handleSaveSecret = async () => {
    try {
      const response = await axiosInstance.post('/integration/shopify/tenant-secret', {
        TenantId: selectedTenantId,
        ClientId: clientId,
        ClientSecret: clientSecret,
        GenerateShopifyOAuthUrl: true
      });
      if (response.data.statusCode === "OK") {
        setDialog({
          open: true,
          title: "Success",
          message: "API credentials saved successfully!",
        });
        const authUrl = response.data.data;
        window.open(authUrl, '_blank');
        await handleTenantSelect(selectedTenantId);
      } else {
        setDialog({
          open: true,
          title: "Error",
          message: response.data.errorMessage || "Failed to save API credentials.",
        });
      }
    } catch (error) {
      console.error("Error saving API credentials:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to save API credentials.",
      });
    }
  };
  // New handler to just generate and show URL
  const handleGenerateInstallUrl = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setDialog({
        open: true,
        title: "Input Required",
        message: "Please enter both Client ID and Client Secret.",
      });
      return;
    }

    try {
      // Clear previous URL if any
      setGeneratedUrl("");

      const response = await axiosInstance.post('/integration/shopify/tenant-secret', {
        TenantId: selectedTenantId,
        ClientId: clientId,
        ClientSecret: clientSecret,
        GenerateShopifyOAuthUrl: true
      });

      if (response.data.statusCode === "OK") {
        setGeneratedUrl(response.data.data); // Store URL to show in UI
        // Success dialog removed as requested to "don't do anything else" other than show label
      } else {
        setDialog({
          open: true,
          title: "Error",
          message: response.data.errorMessage || "Failed to generate URL.",
        });
      }
    } catch (error) {
      console.error("Error generating URL:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to generate URL.",
      });
    }
  };

  const handleDeleteTenant = async () => {
    setDeletingTenant(true);
    try {
      await axiosInstance.delete(`/tenant/${selectedTenantId}`);
      setDialog({
        open: true,
        title: "Success",
        message: `Tenant "${tenantDetails?.name}" deleted successfully!`,
      });
      setSelectedTenantId("");
      setTenantDetails(null);
      refreshTenants();
    } catch (error) {
      console.error("Error deleting tenant:", error);
      setDialog({
        open: true,
        title: "Error",
        message: "Failed to delete tenant. Please try again later.",
      });
    } finally {
      setDeletingTenant(false);
      setDeleteDialogOpen(false);
    }
  };
  const handleCloseDialog = () => {
    setDialog({ ...dialog, open: false });
  };

  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;
    const safeText = String(text ?? "");
    const lowerText = safeText.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let cursor = 0;
    let matchIndex = lowerText.indexOf(lowerQuery, cursor);

    while (matchIndex !== -1) {
      if (matchIndex > cursor) parts.push(safeText.slice(cursor, matchIndex));
      parts.push(
        <Box
          component="span"
          key={matchIndex}
          sx={{
            px: 0.25,
            borderRadius: 0.75,
            backgroundColor: "rgba(251, 191, 36, 0.88)",
            color: "#1e293b",
            fontWeight: 700,
          }}
        >
          {safeText.slice(matchIndex, matchIndex + query.length)}
        </Box>
      );
      cursor = matchIndex + query.length;
      matchIndex = lowerText.indexOf(lowerQuery, cursor);
    }

    if (cursor < safeText.length) parts.push(safeText.slice(cursor));
    return parts;
  };

  const renderJsonContent = (content, query) => {
    const keyPattern = /"(?:\\.|[^"\\])*"(?=\s*:)/g;
    const parts = [];
    let cursor = 0;
    let match = keyPattern.exec(content);

    while (match) {
      if (match.index > cursor) {
        parts.push(
          <React.Fragment key={`text-${cursor}`}>
            {highlightMatch(content.slice(cursor, match.index), query)}
          </React.Fragment>
        );
      }
      parts.push(
        <Box component="span" key={`key-${match.index}`} sx={{ color: "#fb923c", fontWeight: 700 }}>
          {highlightMatch(match[0], query)}
        </Box>
      );
      cursor = match.index + match[0].length;
      match = keyPattern.exec(content);
    }

    if (cursor < content.length) {
      parts.push(
        <React.Fragment key={`text-${cursor}`}>
          {highlightMatch(content.slice(cursor), query)}
        </React.Fragment>
      );
    }

    return parts;
  };

  const parseCsvLine = (line) => {
    const values = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        values.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values.map((value) => value.replace(/^"|"$/g, ""));
  };

  const handleOpenCsvPreview = async () => {
    const feedUrl = tenantDetails?.dataFeedConnector?.configurations?.dataFeedUrl;
    const dataType = resolveFeedDataType(
      feedUrl,
      tenantDetails?.dataFeedConnector?.configurations?.dataType
    );
    if (!feedUrl) return;

    setFeedPreviewType(dataType);
    setCsvPreviewOpen(true);
    setLoadingCsvPreview(true);
    setCsvPreviewData(null);
    setJsonPreviewContent(null);
    setJsonPreviewData(null);

    try {
      const response = await fetch(feedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const text = await response.text();

      if (dataType === "json") {
        const json = JSON.parse(text);
        setJsonPreviewContent(JSON.stringify(json, null, 2));
        setJsonPreviewData(json);
      } else if (dataType === "xml") {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        const root = xmlDoc.documentElement;
        const itemNodes = root.children.length > 0 ? Array.from(root.children) : [];
        if (itemNodes.length === 0) {
          setCsvPreviewData({ headers: [], rows: [] });
          return;
        }
        const headers = [...new Set(itemNodes.flatMap((node) =>
          Array.from(node.children).map((child) => child.tagName)
        ))];
        const rows = itemNodes.map((node) =>
          headers.map((tag) => {
            const child = node.querySelector(tag);
            if (!child) return "";
            return child.children.length > 0 ? child.innerHTML : child.textContent || "";
          })
        );
        setCsvPreviewData({ headers, rows });
      } else {
        const lines = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line !== "");

        if (lines.length === 0) {
          setCsvPreviewData({ headers: [], rows: [] });
          return;
        }

        const headers = parseCsvLine(lines[0]);
        const idColIndex = headers.findIndex((header) => header.toLowerCase() === "id");
        const rows = lines.slice(1).map((line) => {
          const values = parseCsvLine(line);
          return values.map((value, idx) => {
            if (idx === idColIndex && /^[\d.]+[eE]\+?\d+$/.test(value)) {
              return Number(value).toLocaleString("fullwide", { useGrouping: false });
            }
            return value;
          });
        });

        setCsvPreviewData({ headers, rows });
      }
    } catch (error) {
      console.error("Error fetching file preview:", error);
      setCsvPreviewData(null);
      setJsonPreviewContent(null);
      setJsonPreviewData(null);
    } finally {
      setLoadingCsvPreview(false);
    }
  };

  const handleCloseCsvPreview = () => {
    setCsvPreviewOpen(false);
    setCsvPreviewData(null);
    setJsonPreviewContent(null);
    setJsonPreviewData(null);
    setCsvSearchQuery("");
    setCsvSelectedColumns([]);
    setCsvVisibleRows(100);
  };

  const handleTestRules = async () => {
    if (!selectedTenantId || !importSettings?.rules?.length) return;

    setTestRulesLoading(true);
    setTestRulesResults(null);
    setTestRulesRawProduct(null);

    try {
      const { results, rawProduct } = await requestEnrichmentPreview(
        selectedTenantId,
        importSettings.rules,
        testRulesProductId,
        {
          feedConfigurations: tenantDetails?.dataFeedConnector?.configurations,
          productIdFieldName: importSettings.productIdFieldName,
        }
      );
      setTestRulesResults(results);
      setTestRulesRawProduct(rawProduct || null);
    } catch (error) {
      console.error("Error testing rules:", error);
      const message = getEnrichmentPreviewErrorMessage(error);
      setTestRulesResults(
        importSettings.rules.map(() => ({ error: message }))
      );
    } finally {
      setTestRulesLoading(false);
    }
  };

  const handleRetestRule = async (rule, sourceFieldEdit) => {
    setRuleDetailRetesting(true);
    try {
      if (sourceFieldEdit !== undefined) {
        return evaluateEnrichmentRules(applyEnrichmentPreviewFieldEdit(testRulesRawProduct, sourceFieldEdit), [rule])[0];
      }
      const { results } = await requestEnrichmentPreview(
        selectedTenantId,
        [rule],
        testRulesProductId,
        {
          feedConfigurations: tenantDetails?.dataFeedConnector?.configurations,
          productIdFieldName: importSettings.productIdFieldName,
        }
      );
      return results[0];
    } catch (error) {
      return { error: getEnrichmentPreviewErrorMessage(error) };
    } finally {
      setRuleDetailRetesting(false);
    }
  };

  const currentTenant = tenants.find((t) => t.id === selectedTenant) || null;
  const actionButtonBaseSx = {
    minHeight: 44,
    px: 2.25,
    borderRadius: "14px",
    textTransform: "none",
    fontWeight: 700,
    letterSpacing: "-0.01em",
  };
  const primaryButtonSx = {
    ...actionButtonBaseSx,
    color: "#fff",
    background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
    boxShadow: "0 14px 30px rgba(255, 139, 71, 0.28)",
    "&:hover": {
      background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)",
      boxShadow: "0 18px 36px rgba(255, 139, 71, 0.34)",
    },
  };
  const secondaryButtonSx = {
    ...actionButtonBaseSx,
    color: "#f97316",
    borderColor: "rgba(249, 115, 22, 0.26)",
    backgroundColor: "rgba(255, 247, 241, 0.86)",
    "&:hover": {
      borderColor: "rgba(249, 115, 22, 0.42)",
      backgroundColor: "rgba(255, 241, 233, 0.96)",
    },
  };
  const neutralButtonSx = {
    ...actionButtonBaseSx,
    color: "#4b5563",
    borderColor: "rgba(15, 23, 42, 0.10)",
    backgroundColor: "rgba(255,255,255,0.82)",
    "&:hover": {
      borderColor: "rgba(249, 115, 22, 0.22)",
      backgroundColor: "rgba(255, 247, 241, 0.88)",
      color: "#f97316",
    },
  };
  const dangerButtonSx = {
    ...actionButtonBaseSx,
    color: "#fff",
    background: "linear-gradient(180deg, #fb7185 0%, #ef4444 100%)",
    boxShadow: "0 14px 28px rgba(239, 68, 68, 0.22)",
    "&:hover": {
      background: "linear-gradient(180deg, #f43f5e 0%, #dc2626 100%)",
      boxShadow: "0 18px 34px rgba(239, 68, 68, 0.28)",
    },
  };
  const themedFieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 44,
      borderRadius: "12px",
      backgroundColor: "rgba(255,255,255,0.96)",
      "& fieldset": {
        borderColor: "rgba(15, 23, 42, 0.10)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(249, 115, 22, 0.35)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#fb923c",
        borderWidth: "1px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f97316",
    },
  };
  const softCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(255, 132, 76, 0.10)",
    backgroundColor: "rgba(255,255,255,0.82)",
    boxShadow: "0 14px 30px rgba(236, 123, 55, 0.06)",
  };
  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1, md: 2 },
        py: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2.25,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              width: { xs: 56, md: 64 },
              height: { xs: 56, md: 64 },
              borderRadius: "20px",
              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
              boxShadow: "0 18px 38px rgba(255, 139, 71, 0.30)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <SettingsOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2rem", md: "2.55rem" },
                lineHeight: 1.05,
                fontWeight: 800,
                color: "#202531",
                letterSpacing: "-0.03em",
                mb: 0.8,
              }}
            >
              Tenant Management
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6b7280",
                fontWeight: 500,
              }}
            >
              Browse tenant details, update configuration, and manage import settings.
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mb: 3.25,
            p: { xs: 2, md: 3 },
            borderRadius: "24px",
            border: "1px solid rgba(255, 132, 76, 0.10)",
            backgroundColor: "rgba(255,255,255,0.88)",
            boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#6b7280",
              fontWeight: 700,
              mb: 0.9,
              px: 0.5,
            }}
          >
            Select Tenant
          </Typography>
          <TenantAutocomplete
            tenants={tenants}
            value={currentTenant}
            onChange={(event, newValue) => {
              if (newValue) {
                handleTenantSelect(newValue.id);
              } else {
                setSelectedTenant("");
                setSelectedTenantId("");
                setTenantDetails(null);
              }
            }}
            placeholder="Choose a tenant"
            sx={{ width: "100%" }}
          />
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.75, mt: -1.5 }}>
          <Box
            sx={{
              display: "inline-flex",
              gap: 0.5,
              alignItems: "center",
              p: 0.3,
              borderRadius: "12px",
              backgroundColor: "rgba(255, 247, 241, 0.88)",
              border: "1px solid rgba(255, 132, 76, 0.10)",
            }}
          >
            <Button
              size="small"
              variant={layoutMode === "old" ? "contained" : "text"}
              onClick={() => setLayoutMode("old")}
              sx={layoutMode === "old" ? { ...primaryButtonSx, px: 1.25, py: 0, fontSize: "0.7rem", minWidth: 0, minHeight: 0, lineHeight: 1.2, height: 22 } : { ...neutralButtonSx, px: 1.25, py: 0, fontSize: "0.7rem", minWidth: 0, minHeight: 0, lineHeight: 1.2, height: 22 }}
            >
              Old Layout
            </Button>
            <Button
              size="small"
              variant={layoutMode === "new" ? "contained" : "text"}
              onClick={() => setLayoutMode("new")}
              sx={layoutMode === "new" ? { ...primaryButtonSx, px: 1.25, py: 0, fontSize: "0.7rem", minWidth: 0, minHeight: 0, lineHeight: 1.2, height: 22 } : { ...neutralButtonSx, px: 1.25, py: 0, fontSize: "0.7rem", minWidth: 0, minHeight: 0, lineHeight: 1.2, height: 22 }}
            >
              New Layout
            </Button>
          </Box>
        </Box>

        {loadingDetails ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress sx={{ color: "#f97316" }} />
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 1.5, fontWeight: 600 }}>
              Loading tenant details...
            </Typography>
          </Box>
        ) : tenantDetails ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, md: 2 },
              borderRadius: "22px",
              overflow: "hidden",
              border: "1px solid rgba(255, 132, 76, 0.10)",
              boxShadow: "0 24px 60px rgba(236, 123, 55, 0.08)",
              backgroundColor: "rgba(255,255,255,0.92)",
            }}
          >
            {layoutMode === "new" ? (
              <TenantManagementModernLayout
                tenantDetails={tenantDetails}
                setTenantDetails={setTenantDetails}
                handleSaveTenant={handleSaveTenant}
                onShopifyCredentialsSaved={() => handleTenantSelect(selectedTenantId, { preserveCurrentView: true })}
                savingTenant={savingTenant}
                jsonError={jsonError}
                handleOpenCsvPreview={handleOpenCsvPreview}
                handleClearTenantCache={handleClearCache}
                clearingTenantCache={clearingCache}
                primaryButtonSx={primaryButtonSx}
                neutralButtonSx={neutralButtonSx}
                importSettings={importSettings}
                setImportSettings={setImportSettings}
                isImportSettingsDirty={isImportSettingsDirty}
                setIsImportSettingsDirty={setIsImportSettingsDirty}
                availableFieldNames={availableFieldNames}
                handleImportSettingTextFieldChange={handleImportSettingTextFieldChange}
                handleHasParentProductChange={handleHasParentProductChange}
                handleUsePreezieGeneratedIdChange={handleUsePreezieGeneratedIdChange}
                handleUseAggregationValueAsDocumentIdChange={handleUseAggregationValueAsDocumentIdChange}
                handleRuleChange={handleRuleChange}
                handleAddRule={handleAddRule}
                handleRemoveRule={handleRemoveRule}
                handleResetImportSettings={handleResetImportSettings}
                handleSaveImportSettings={handleSaveImportSettings}
                isAggregationRulePresent={isAggregationRulePresent}
                handleTestRules={handleTestRules}
                testRulesProductId={testRulesProductId}
                setTestRulesProductId={setTestRulesProductId}
                testRulesLoading={testRulesLoading}
                testRulesResults={testRulesResults}
                testRulesRawProduct={testRulesRawProduct}
                handleRetestRule={handleRetestRule}
                ruleDetailRetesting={ruleDetailRetesting}
                ruleDetailOpen={ruleDetailOpen}
                setRuleDetailOpen={setRuleDetailOpen}
                ruleDetailData={ruleDetailData}
                setRuleDetailData={setRuleDetailData}
                activeSection={activeTenantSection}
                setActiveSection={setActiveTenantSection}
                expandedGroups={expandedTenantSectionGroups}
                setExpandedGroups={setExpandedTenantSectionGroups}
              />
            ) : (
              <>
                <Box sx={{ borderBottom: "1px solid rgba(255, 132, 76, 0.10)" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="tenant details tabs"
                    variant="scrollable"
                    scrollButtons={false}
                    sx={{
                      px: { xs: 0.5, md: 1 },
                      "& .MuiTabs-indicator": {
                        height: 3,
                        borderRadius: "999px",
                        background: "linear-gradient(90deg, #ff8a3d, #ff6d1b)",
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        minHeight: 0,
                        minWidth: "auto",
                        px: { xs: 1.25, md: 1.75 },
                        py: 1.4,
                        borderRadius: "14px",
                        color: "#4b5563",
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        "&:hover": {
                          color: "#f97316",
                          backgroundColor: "rgba(255, 247, 241, 0.72)",
                        },
                        "&.Mui-selected": {
                          color: "#f97316",
                        },
                      },
                    }}
                  >
                    <Tab label="Overview" {...a11yProps(0)} />
                    <Tab label="Tenant Details (JSON)" {...a11yProps(1)} />
                    <Tab label="Import Settings" {...a11yProps(2)} />
                  </Tabs>
                </Box>
                <TabPanel value={tabValue} index={0}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Tenant Overview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <CopyableTypography
                          label="ID"
                          value={tenantDetails?.id}
                          onCopy={handleCopy}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CopyableTypography
                          label="Name"
                          value={tenantDetails?.name}
                          onCopy={handleCopy}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CopyableTypography
                          label="Website URL"
                          value={tenantDetails?.websiteUrl}
                          onCopy={handleCopy}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CopyableTypography
                          label="Elastic Index Name"
                          value={tenantDetails?.tenantAiProfile?.elasticIndexName}
                          onCopy={handleCopy}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CopyableTypography
                          label="Policy Index Name"
                          value={tenantDetails?.tenantAiProfile?.policyIndexName}
                          onCopy={handleCopy}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <CopyableTypography
                          label="Webscraper Index"
                          value={tenantDetails?.webScrapeIndexName}
                          onCopy={handleCopy}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: "1fr 2fr",
                            },
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(2, minmax(0, 1fr))",
                              },
                              gap: 2,
                            }}
                          >
                            <CopyableTypography
                              label="Data Feed"
                              value={
                                tenantDetails?.dataFeedConnector?.configurations?.dataFeed
                              }
                              onCopy={handleCopy}
                            />
                            <CopyableTypography
                              label="Data Type"
                              value={
                                tenantDetails?.dataFeedConnector?.configurations?.dataType
                              }
                              onCopy={handleCopy}
                            />
                          </Box>
                          <Box>
                            <Typography component="div">
                              <strong>Data Feed URL:</strong>{" "}
                              <Box
                                component="span"
                                onClick={() =>
                                  handleCopy(
                                    tenantDetails?.dataFeedConnector?.configurations?.dataFeedUrl || "N/A"
                                  )
                                }
                                sx={{
                                  cursor: "pointer",
                                  wordBreak: "break-all",
                                  "&:hover": {
                                    textDecoration: "underline",
                                    color: "primary.main",
                                  },
                                }}
                              >
                                {tenantDetails?.dataFeedConnector?.configurations?.dataFeedUrl || "N/A"}
                              </Box>
                              {tenantDetails?.dataFeedConnector?.configurations?.dataFeedUrl &&
                                (tenantDetails?.dataFeedConnector?.configurations?.dataType?.toLowerCase() === "csv" ||
                                  tenantDetails?.dataFeedConnector?.configurations?.dataType?.toLowerCase() === "json" ||
                                  tenantDetails?.dataFeedConnector?.configurations?.dataType?.toLowerCase() === "xml") && (
                                  <Tooltip title={`Preview ${tenantDetails?.dataFeedConnector?.configurations?.dataType?.toUpperCase()}`}>
                                    <IconButton
                                      size="small"
                                      onClick={handleOpenCsvPreview}
                                      sx={{
                                        ml: 0.75,
                                        color: "#f97316",
                                        verticalAlign: "middle",
                                      }}
                                    >
                                      <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              {tenantDetails?.dataFeedConnector?.configurations?.dataFeedUrl && (
                                <Tooltip title="Download file">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={tenantDetails?.dataFeedConnector?.configurations?.dataFeedUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    download
                                    sx={{
                                      ml: 0.5,
                                      color: "#f97316",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleClearCache}
                          disabled={!selectedTenantId || clearingCache}
                          sx={primaryButtonSx}
                        >
                          {clearingCache ? <CircularProgress size={24} color="inherit" /> : "Clear Tenant Cache"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <Typography variant="h6" gutterBottom>
                    Edit Tenant Details (JSON)
                  </Typography>
                  <Editor
                    height="400px"
                    defaultLanguage="json"
                    value={JSON.stringify(tenantDetails, null, 2)}
                    onChange={handleDetailsChange}
                    options={{
                      minimap: { enabled: false },
                      formatOnType: true,
                      scrollBeyondLastLine: false,
                    }}
                  />
                  {jsonError && (
                    <Typography color="error" mt={1}>
                      {jsonError}
                    </Typography>
                  )}
                  <Box mt={3} textAlign="right">
                    {isAdmin && (
                    deletingTenant ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={{ ...dangerButtonSx, mr: 1 }}
                      >
                        Delete
                      </Button>
                    )
                    )}
                    {/* New Save Tenant Button */}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveTenant}
                      disabled={!!jsonError || savingTenant}
                      sx={{ ...primaryButtonSx, mr: 1 }}
                    >
                      {savingTenant ? <CircularProgress size={24} color="inherit" /> : "Save Tenant"}
                    </Button>

                    {isDraft && (
                      <>
                        <Button
                          variant="contained"
                          onClick={handleDeleteDraft}
                          sx={{ ...secondaryButtonSx, mr: 1 }}
                        >
                          Delete Draft variables
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleDeleteDraftWidgetConfigs}
                          sx={{ ...secondaryButtonSx, mr: 1 }}
                        >
                          Delete Draft widget
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePublishDraft}
                          sx={{ ...primaryButtonSx, mr: 1 }}
                        >
                          Publish Variables
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePublishDraftWidgetConfigs}
                          sx={{ ...primaryButtonSx, mr: 1 }}
                        >
                          Publish Widget Configs
                        </Button>
                      </>
                    )}
                    {/* <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!!jsonError}
                style={{ marginRight: 5 }}
              >
                Save Variables
              </Button> */}
                    {/* <Button
                variant="contained"
                color="primary"
                onClick={handleSaveWidgetConfigs}
                disabled={!!jsonError}
                style={{ marginRight: 5 }}
              >
                Save Widget configs
              </Button> */}
                  </Box>
                  <Box mt={4}>
                    <Typography variant="h6" gutterBottom>
                      Shopify Access (OAuth)
                    </Typography>
                    <TextField
                      label="Enter Client ID"
                      variant="outlined"
                      fullWidth
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                    />
                    <TextField
                      label="Enter Client Secret"
                      variant="outlined"
                      fullWidth
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      sx={{ mt: 2 }}
                    />
                    <Box textAlign="right" mt={2} display="flex" justifyContent="flex-end" gap={2}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleGenerateInstallUrl}
                        sx={secondaryButtonSx}
                      >
                        Save secret and generate tenant install URL
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveSecret}
                        sx={primaryButtonSx}
                      >
                        Save Secret and Call Shopify OAuth
                      </Button>
                    </Box>
                    {generatedUrl && (
                      <Box mt={2} p={2} bgcolor="background.paper" borderRadius={1} border={1} borderColor="divider">
                        <CopyableTypography
                          label="Generated Tenant Install URL"
                          value={generatedUrl}
                          onCopy={handleCopy}
                        />
                      </Box>
                    )}
                  </Box>
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  {importSettings ? (
                    <Box component="form" noValidate autoComplete="off">
                      <Paper elevation={0} sx={{ ...softCardSx, p: { xs: 1.5, md: 2 }, mb: 2.25 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", mb: 0.5 }}>
                          Import Enrichment Settings
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1.5 }}>
                          Configure source mapping fields and enrichment behavior for this tenant.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Product ID Field Name"
                              name="productIdFieldName"
                              value={importSettings.productIdFieldName || ''}
                              onChange={handleImportSettingTextFieldChange}
                              sx={themedFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Title Field Name"
                              name="titleFieldName"
                              value={importSettings.titleFieldName || ''}
                              onChange={handleImportSettingTextFieldChange}
                              sx={themedFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: "14px",
                                backgroundColor: "rgba(255, 247, 241, 0.86)",
                                border: "1px solid rgba(255, 132, 76, 0.10)",
                                display: "inline-flex",
                              }}
                            >
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={importSettings.hasParentProduct || false}
                                    onChange={handleHasParentProductChange}
                                    name="hasParentProduct"
                                    sx={{
                                      color: "rgba(249, 115, 22, 0.4)",
                                      "&.Mui-checked": {
                                        color: "#f97316",
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Typography sx={{ color: "#202531", fontWeight: 700 }}>
                                    Has Parent Product
                                  </Typography>
                                }
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: "14px",
                                backgroundColor: "rgba(255, 247, 241, 0.86)",
                                border: "1px solid rgba(255, 132, 76, 0.10)",
                                display: "inline-flex",
                              }}
                            >
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={importSettings.usePreezieGeneratedId || false}
                                    onChange={handleUsePreezieGeneratedIdChange}
                                    name="usePreezieGeneratedId"
                                    sx={{
                                      color: "rgba(249, 115, 22, 0.4)",
                                      "&.Mui-checked": { color: "#f97316" },
                                    }}
                                  />
                                }
                                label={
                                  <Typography sx={{ color: "#202531", fontWeight: 700 }}>
                                    Use Preezie Generated ID
                                  </Typography>
                                }
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: "14px",
                                backgroundColor: "rgba(255, 247, 241, 0.86)",
                                border: "1px solid rgba(255, 132, 76, 0.10)",
                                display: "inline-flex",
                              }}
                            >
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    checked={importSettings.useAggregationValueAsDocumentId || false}
                                    onChange={handleUseAggregationValueAsDocumentIdChange}
                                    name="useAggregationValueAsDocumentId"
                                    sx={{
                                      color: "rgba(249, 115, 22, 0.4)",
                                      "&.Mui-checked": { color: "#f97316" },
                                    }}
                                  />
                                }
                                label={
                                  <Typography sx={{ color: "#202531", fontWeight: 700 }}>
                                    Use Aggregation Value As Document ID
                                  </Typography>
                                }
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", md: "center" },
                          gap: 2,
                          flexWrap: "wrap",
                          mb: 0.9,
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                            Rules
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.1, fontSize: "0.84rem" }}>
                            Manage transformation and aggregation rules applied during import.
                          </Typography>
                        </Box>
                        <Button
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={handleAddRule}
                          variant="outlined"
                          sx={secondaryButtonSx}
                        >
                          Add Rule
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          ...softCardSx,
                          p: { xs: 1, md: 1.15 },
                          mb: 1.25,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "stretch", md: "center" },
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700, alignSelf: "center" }}>
                          Enter a Product ID, or leave it blank to use the first active, published Shopify product.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                          <TextField
                            size="small"
                            placeholder="Product ID (optional)"
                            value={testRulesProductId}
                            onChange={(event) => setTestRulesProductId(event.target.value)}
                            sx={{ width: { xs: "100%", sm: 200 }, ...themedFieldSx }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleTestRules}
                            disabled={testRulesLoading || !(importSettings.rules || []).length}
                            sx={{
                              minHeight: 38,
                              px: 1.75,
                              borderRadius: "12px",
                              textTransform: "none",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              color: "#fff",
                              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                              boxShadow: "0 12px 24px rgba(255, 139, 71, 0.22)",
                              "&:hover": {
                                background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)",
                              },
                            }}
                          >
                            {testRulesLoading ? <CircularProgress size={14} color="inherit" /> : "Test Rules"}
                          </Button>
                        </Box>
                      </Box>
                      {(importSettings.rules || []).map((rule, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            ...softCardSx,
                            p: { xs: 1, md: 1.15 },
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 0.65,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "#202531", fontWeight: 800, letterSpacing: "0.02em" }}>
                              Rule {index + 1}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              {testRulesResults && testRulesResults[index] && (
                                <Chip
                                  size="small"
                                  label={(() => {
                                    const text = testRulesResults[index].error || testRulesResults[index].value || "";
                                    return text.length > 18 ? `${text.slice(0, 18)}...` : text;
                                  })()}
                                  color={testRulesResults[index].error ? "error" : "success"}
                                  onClick={() => {
                                    setRuleDetailData(createEnrichmentRuleDetail(rule, testRulesResults[index], index, testRulesRawProduct));
                                    setRuleDetailOpen(true);
                                  }}
                                  sx={{
                                    maxWidth: 180,
                                    cursor: "pointer",
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                />
                              )}
                              <IconButton
                                onClick={() => handleRemoveRule(index)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "10px",
                                  color: "#ef4444",
                                  backgroundColor: "rgba(254, 242, 242, 0.9)",
                                  border: "1px solid rgba(239, 68, 68, 0.12)",
                                  "&:hover": {
                                    backgroundColor: "rgba(254, 226, 226, 0.96)",
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                          <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} sm={3} md={2}>
                              <FormControl fullWidth sx={themedFieldSx}>
                                <InputLabel>Type</InputLabel>
                                <Select
                                  size="small"
                                  value={rule.type || ''}
                                  label="Type"
                                  onChange={(e) => handleRuleChange(index, 'type', e.target.value)}
                                >
                                  <MenuItem
                                    value="Aggregation"
                                    disabled={isAggregationRulePresent && rule.type !== 'Aggregation'}
                                  >
                                    Aggregation
                                  </MenuItem>
                                  <MenuItem value="FieldValue">FieldValue</MenuItem>
                                  <MenuItem value="ParentProductAggregation" disabled={!importSettings.hasParentProduct}>
                                    ParentProductAggregation
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {rule.type !== 'Aggregation' && rule.type !== 'ParentProductAggregation' && (
                              <Grid item xs={12} sm={4} md={3}>
                                <Autocomplete
                                  size="small"
                                  options={availableFieldNames || []}
                                  value={rule.fieldName || null}
                                  onChange={(event, newValue) => {
                                    handleRuleChange(index, 'fieldName', newValue);
                                  }}
                                  renderInput={(params) => <TextField {...params} label="Field Name" sx={themedFieldSx} />}
                                  sx={themedFieldSx}
                                />
                              </Grid>
                            )}

                            <Grid item xs={12} sm={rule.type !== 'Aggregation' && rule.type !== 'ParentProductAggregation' ? 4 : 8} md={rule.type !== 'Aggregation' && rule.type !== 'ParentProductAggregation' ? 6 : 9}>
                              <TextField
                                size="small"
                                label="Script"
                                fullWidth
                                multiline
                                minRows={2}
                                value={rule.script || ''}
                                onChange={(e) => handleRuleChange(index, 'script', e.target.value)}
                                sx={themedFieldSx}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                      <Box sx={{ mt: 2.25, display: 'flex', justifyContent: 'flex-end', gap: 1.25, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          onClick={handleResetImportSettings}
                          sx={neutralButtonSx}
                          disabled={!isImportSettingsDirty}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSaveImportSettings}
                          disabled={!isImportSettingsDirty}
                          sx={primaryButtonSx}
                        >
                          Save Settings
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography>Loading import settings...</Typography>
                  )}
                </TabPanel>
              </>
            )}
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "22px",
              border: "1px solid rgba(255, 132, 76, 0.10)",
              boxShadow: "0 24px 60px rgba(236, 123, 55, 0.08)",
              backgroundColor: "rgba(255,255,255,0.92)",
              py: 6,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" sx={{ color: "#4b5563", fontWeight: 700 }}>
              Select a tenant to view and edit details.
            </Typography>
          </Paper>
        )}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog open={dialog.open} onClose={handleCloseDialog} disableScrollLock>
        <DialogTitle>{dialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {layoutMode === "old" && (
        <Dialog
          open={ruleDetailOpen}
          onClose={() => setRuleDetailOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
          sx: {
            borderRadius: "28px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
          }}
        >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
              Rule Detail
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
              Inspect the rule, edit the script, and validate the result.
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setRuleDetailOpen(false)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "14px",
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#fff7f1",
                color: "#f97316",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            backgroundColor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          {ruleDetailData && (
            <Box
              sx={{
                p: 2.25,
                borderRadius: "22px",
                border: "1px solid rgba(255, 132, 76, 0.10)",
                backgroundColor: "rgba(255,255,255,0.82)",
                boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
              }}
            >
              <Box display="flex" gap={3} flexWrap="wrap" mb={1.5}>
                <Box sx={{ minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Type</Typography>
                  <Typography variant="body1" sx={{ color: "#202531", fontWeight: 500 }}>{ruleDetailData.rule.type}</Typography>
                </Box>
                {ruleDetailData.rule.fieldName && (
                  <Box sx={{ minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Field Name</Typography>
                    <Typography variant="body1" sx={{ color: "#202531", fontWeight: 500 }}>{ruleDetailData.rule.fieldName}</Typography>
                  </Box>
                )}
              </Box>
              <EnrichmentPreviewSourceEditor ruleDetailData={ruleDetailData} setRuleDetailData={setRuleDetailData} disabled={ruleDetailRetesting} />
              <Box mb={1.5}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Script</Typography>
                <TextField
                  label="Rule script"
                  fullWidth
                  multiline
                  minRows={3}
                  value={ruleDetailData.rule.script || ""}
                  onChange={(e) =>
                    setRuleDetailData((prev) => ({
                      ...prev,
                      rule: { ...prev.rule, script: e.target.value },
                    }))
                  }
                  sx={{
                    mt: 0.75,
                    ...themedFieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...themedFieldSx["& .MuiOutlinedInput-root"],
                      fontFamily: "monospace",
                      fontSize: "0.88rem",
                    },
                  }}
                />
              </Box>
              <Box mb={1.5} display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={ruleDetailRetesting || !!ruleDetailData.sourceFieldError}
                  onClick={async () => {
                    const result = await handleRetestRule(ruleDetailData.rule, getEnrichmentPreviewSourceFieldEdit(ruleDetailData));
                    setRuleDetailData((prev) => ({ ...prev, result }));
                  }}
                  sx={secondaryButtonSx}
                >
                  {ruleDetailRetesting ? <CircularProgress size={14} /> : "Re-test"}
                </Button>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Result</Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "monospace",
                    bgcolor: ruleDetailData.result?.error ? "rgba(254, 242, 242, 0.96)" : "rgba(240, 253, 244, 0.96)",
                    color: ruleDetailData.result?.error ? "#dc2626" : "#15803d",
                    p: 1.5,
                    borderRadius: 2,
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    border: "1px solid",
                    borderColor: ruleDetailData.result?.error ? "rgba(248, 113, 113, 0.30)" : "rgba(74, 222, 128, 0.30)",
                    mt: 0.75,
                  }}
                >
                  {ruleDetailData.result?.error || ruleDetailData.result?.value || "(empty)"}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        </Dialog>
      )}
      <Dialog
        open={csvPreviewOpen}
        onClose={handleCloseCsvPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "28px",
            border: "1px solid rgba(255, 132, 76, 0.12)",
            background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.20), rgba(255,255,255,0.98) 38%), linear-gradient(180deg, rgba(255, 248, 243, 0.98), rgba(255,255,255,0.96))",
            boxShadow: "0 30px 70px rgba(236, 123, 55, 0.16)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
                boxShadow: "0 14px 28px rgba(255, 139, 71, 0.24)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#202531", lineHeight: 1.1 }}>
                {feedPreviewType.toUpperCase()} Preview
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.35 }}>
                {feedPreviewType === "json"
                  ? "Inspect the formatted JSON feed structure."
                  : "Search rows, filter columns, and inspect feed contents."}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseCsvPreview}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "14px",
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#fff7f1",
                color: "#f97316",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            backgroundColor: "transparent",
            borderTop: 0,
            borderBottom: 0,
          }}
        >
          {loadingCsvPreview ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : feedPreviewType === "json" && jsonPreviewContent ? (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Search in JSON"
                placeholder="Search keys or values..."
                value={csvSearchQuery}
                onChange={(e) => setCsvSearchQuery(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    minHeight: 52,
                    borderRadius: "14px",
                    backgroundColor: "#fff",
                    "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                    "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                    "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                  },
                }}
              />
              <Typography variant="caption" sx={{ display: "block", mb: 1.25, color: "#64748b", fontWeight: 700 }}>
                Showing {filteredJsonPreview.shownItems} of {filteredJsonPreview.totalItems} items
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2.25,
                  maxHeight: "65vh",
                  overflow: "auto",
                  borderRadius: "22px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  background: "linear-gradient(145deg, #172033 0%, #1e293b 100%)",
                  color: "#e2e8f0",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontSize: "0.8rem",
                  lineHeight: 1.65,
                  whiteSpace: "pre",
                }}
              >
                {renderJsonContent(filteredJsonPreview.content, csvSearchQuery)}
              </Box>
            </Box>
          ) : csvPreviewData ? (
            <Box
              sx={{
                p: 2.25,
                borderRadius: "22px",
                border: "1px solid rgba(255, 132, 76, 0.10)",
                backgroundColor: "rgba(255,255,255,0.82)",
                boxShadow: "0 18px 38px rgba(236, 123, 55, 0.07)",
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search in ${feedPreviewType.toUpperCase()}...`}
                  value={csvSearchQuery}
                  onChange={(e) => setCsvSearchQuery(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 52,
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                    },
                  }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  options={csvPreviewData.headers}
                  value={csvSelectedColumns}
                  onChange={(_, newValue) => setCsvSelectedColumns(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Filter Columns" placeholder="Search Columns..." />
                  )}
                  sx={{
                    minWidth: 300,
                    flexShrink: 0,
                    "& .MuiOutlinedInput-root": {
                      minHeight: 52,
                      borderRadius: "14px",
                      backgroundColor: "#fff",
                      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                    },
                  }}
                  limitTags={2}
                />
                <Box sx={{ flexBasis: "100%", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
                  {csvPreviewData.headers.map((header, index) => (
                    <Chip
                      key={`${header}-${index}`}
                      label={header}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        color: "#9a3412",
                        backgroundColor: "rgba(255, 237, 213, 0.78)",
                        border: "1px solid rgba(249, 115, 22, 0.26)",
                      }}
                    />
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, ml: 0.5 }}>
                    {csvPreviewData.rows.length} rows
                  </Typography>
                </Box>
              </Box>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: "18px",
                  border: "1px solid rgba(255, 132, 76, 0.10)",
                  boxShadow: "0 16px 34px rgba(236, 123, 55, 0.06)",
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: "max-content",
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-head": {
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          color: "#f97316",
                          background: "linear-gradient(180deg, rgba(255, 244, 238, 0.98), rgba(255, 247, 243, 0.98))",
                          borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
                        },
                      }}
                    >
                      {csvPreviewData.headers.map((header, idx) => (
                        (csvSelectedColumns.length === 0 || csvSelectedColumns.includes(header)) && (
                          <TableCell key={idx}>{header}</TableCell>
                        )
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const filtered = csvPreviewData.rows.filter((row) => {
                        if (!csvSearchQuery.trim()) return true;
                        const query = csvSearchQuery.toLowerCase();
                        const indicesToSearch = csvSelectedColumns.length > 0
                          ? csvSelectedColumns.map((col) => csvPreviewData.headers.indexOf(col))
                          : csvPreviewData.headers.map((_, i) => i);

                        return indicesToSearch.some((idx) =>
                          String(row[idx] || "").toLowerCase().includes(query)
                        );
                      });

                      const visible = csvSearchQuery.trim()
                        ? filtered
                        : filtered.slice(0, csvVisibleRows);

                      return visible.map((row, rowIdx) => (
                        <TableRow
                          key={rowIdx}
                          sx={{
                            backgroundColor: rowIdx % 2 === 0 ? "rgba(255,255,255,0.92)" : "rgba(255, 247, 241, 0.62)",
                            "&:hover": {
                              backgroundColor: "rgba(255, 241, 233, 0.82)",
                            },
                          }}
                        >
                          {row.map((cell, cellIdx) => {
                            const header = csvPreviewData.headers[cellIdx];
                            if (csvSelectedColumns.length > 0 && !csvSelectedColumns.includes(header)) {
                              return null;
                            }

                            const safeCell = String(cell || "");

                            return (
                              <TableCell key={cellIdx} sx={{ whiteSpace: "nowrap", borderBottom: "1px solid rgba(255, 132, 76, 0.08)" }}>
                                {csvSearchQuery.trim() && safeCell.toLowerCase().includes(csvSearchQuery.toLowerCase())
                                  ? highlightMatch(safeCell, csvSearchQuery)
                                  : safeCell}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              {!csvSearchQuery.trim() && csvVisibleRows < csvPreviewData.rows.length && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => setCsvVisibleRows((prev) => prev + 100)}
                    sx={{
                      minHeight: 42,
                      px: 2.5,
                      borderRadius: "14px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#f97316",
                      borderColor: "rgba(249, 115, 22, 0.26)",
                      backgroundColor: "rgba(255, 247, 241, 0.86)",
                      "&:hover": {
                        borderColor: "rgba(249, 115, 22, 0.42)",
                        backgroundColor: "rgba(255, 241, 233, 0.96)",
                      },
                    }}
                  >
                    Load More ({Math.min(csvVisibleRows, csvPreviewData.rows.length)} / {csvPreviewData.rows.length})
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Failed to load {feedPreviewType.toUpperCase()} preview.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the tenant "{tenantDetails?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary" variant="outlined" sx={neutralButtonSx}>
            Cancel
          </Button>
          <Button onClick={handleDeleteTenant} color="error" autoFocus sx={dangerButtonSx}>
            {deletingTenant ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default TenantsManagementTab;
